import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PartyPopper, Sparkles, ArrowRight, Coins, ShoppingBag } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ScratchCard from "@/components/ScratchCard";

/**
 * RechargeCelebrationModal — premium full-screen success experience shown to the
 * member the moment their SMC recharge is approved by admin.
 *
 * Composition:
 *   1. Confetti (pure CSS, no external library)
 *   2. "SMC credited" hero panel with animated coin
 *   3. Scratch card that awards random Gifto bonus (server-side)
 *   4. "Shop Now" primary CTA → /marketplace  (+ share on WhatsApp secondary)
 *
 * Props:
 *   open         : boolean
 *   notification : { id, data:{ scratch_card_id, smc_credited, amount_inr, plan_label } }
 *   onClose      : called when user dismisses (auto-marks notification read)
 *   onWalletChange: optional callback fired after Gifto is credited so wallet-showing
 *                   parents can refresh their balance in real time.
 */
const CONFETTI_COLORS = ["#E87A9C", "#F5B6C8", "#F09BB3", "#10b981", "#3b82f6", "#f97316"];

function ConfettiPiece({ index }) {
  const left = (index * 37) % 100;
  const delay = ((index * 73) % 1500) / 1000;
  const duration = 2.5 + ((index * 31) % 1500) / 1000;
  const color = CONFETTI_COLORS[index % CONFETTI_COLORS.length];
  const rotate = (index * 47) % 360;
  return (
    <span
      aria-hidden="true"
      style={{
        position: "absolute",
        top: "-12px",
        left: `${left}%`,
        width: "9px",
        height: "16px",
        background: color,
        opacity: 0.9,
        transform: `rotate(${rotate}deg)`,
        animation: `confetti-fall ${duration}s ${delay}s ease-in forwards`,
        borderRadius: "2px",
        pointerEvents: "none",
      }}
    />
  );
}

export default function RechargeCelebrationModal({ open, notification, onClose, onWalletChange }) {
  const nav = useNavigate();
  const pieces = useMemo(() => Array.from({ length: 80 }, (_, i) => i), []);
  const [revealing, setRevealing] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [awarded, setAwarded] = useState(null);
  const data = notification?.data || {};
  const smcCredited = data.smc_credited || 0;
  const planLabel = data.plan_label || "Recharge Plan";
  const amountInr = data.amount_inr || 0;
  const scratchCardId = data.scratch_card_id;

  // Reset scratch state whenever a new notification opens the modal.
  useEffect(() => {
    if (open) {
      setRevealed(false);
      setAwarded(null);
      setRevealing(false);
    }
  }, [open, notification?.id]);

  // Inject confetti keyframes once (reused between modals).
  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const id = "shakti-celebration-keyframes";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
      @keyframes confetti-fall {
        0%   { transform: translateY(0)    rotate(0deg);   opacity: 0; }
        10%  { opacity: 0.9; }
        100% { transform: translateY(780px) rotate(720deg); opacity: 0; }
      }
      @keyframes coin-burst {
        0%   { transform: scale(0)   rotate(-30deg); opacity: 0; }
        60%  { transform: scale(1.15) rotate(8deg);   opacity: 1; }
        100% { transform: scale(1)   rotate(0deg);   opacity: 1; }
      }
      @keyframes coin-glow {
        0%, 100% { box-shadow: 0 0 0 0 rgba(255, 213, 0, 0.5); }
        50%      { box-shadow: 0 0 0 20px rgba(255, 213, 0, 0); }
      }
    `;
    document.head.appendChild(style);
  }, [open]);

  const handleReveal = async () => {
    if (!scratchCardId || revealing || revealed) return;
    setRevealing(true);
    try {
      const r = await api.post(`/scratch-cards/${scratchCardId}/reveal`);
      const bonus = r.data?.bonus_awarded ?? 0;
      setAwarded(bonus);
      setRevealed(true);
      onWalletChange && onWalletChange();
      // Subtle secondary confirmation
      toast.success(`+${bonus} Gifto Coins added to your wallet!`, { duration: 3500 });
    } catch {
      toast.error("Reveal failed — please try again in a moment");
      setRevealing(false);
    }
  };

  const dismiss = async () => {
    // Mark notification read (best-effort, non-blocking)
    if (notification?.id) {
      api.post(`/notifications/${notification.id}/read`).catch(() => {});
    }
    onClose && onClose();
  };

  const shopNow = () => {
    dismiss();
    nav("/marketplace");
  };

  const shareWa = () => {
    const text =
      `🎉 Just recharged my Shakti Wings wallet!\n\n` +
      `Got ${smcCredited} SMC + a Lucky Scratch Bonus 💜\n\n` +
      `Join the sisterhood: https://shaktiwingscommunity.com`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && dismiss()}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden border-0 shadow-2xl"
        data-testid="recharge-celebration-modal"
      >
        <div className="relative bg-gradient-to-br from-amethyst-900 via-magenta-700 to-amethyst-800 text-white p-6 overflow-hidden">
          {/* Confetti layer */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {pieces.map((i) => <ConfettiPiece key={i} index={i} />)}
          </div>

          {/* Content */}
          <div className="relative z-10 flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-full bg-white/15 backdrop-blur-sm border border-white/30 flex items-center justify-center mb-3"
                 style={{ animation: "coin-glow 2s infinite" }}>
              <PartyPopper className="w-8 h-8 text-yellow-300" strokeWidth={1.8} />
            </div>

            <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-yellow-200 mb-1">💜 Recharge Approved</p>
            <h2 className="text-2xl sm:text-3xl font-bold leading-tight mb-1" data-testid="celebration-title">
              Congratulations!
            </h2>
            <p className="text-magenta-100 text-sm mb-4 max-w-xs">
              Your <span className="font-bold text-white">₹{amountInr.toLocaleString("en-IN")} {planLabel}</span> is live
            </p>

            {/* SMC credited coin */}
            <div
              className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-4 flex items-center gap-3 mb-4"
              style={{ animation: "coin-burst 0.6s 0.1s ease-out both" }}
              data-testid="celebration-smc-credited"
            >
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-200 to-yellow-500 flex items-center justify-center shadow-lg flex-shrink-0">
                <Coins className="w-6 h-6 text-yellow-900" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-[10px] uppercase tracking-wider text-magenta-100 font-semibold">Credited Now</p>
                <p className="text-2xl font-bold tabular-nums">+{smcCredited.toLocaleString("en-IN")} <span className="text-sm font-medium">SMC</span></p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-magenta-200">Worth</p>
                <p className="text-sm font-bold">₹{smcCredited.toLocaleString("en-IN")}</p>
              </div>
            </div>

            {/* Scratch card */}
            <div className="w-full mb-4">
              <p className="text-xs text-magenta-100 mb-2 flex items-center justify-center gap-1">
                <Sparkles className="w-3.5 h-3.5 text-yellow-300" />
                <span className="font-semibold">Bonus Lucky Card</span>
                <span className="opacity-70">— win up to 100 Gifto Coins!</span>
              </p>
              <div onPointerDown={handleReveal /* first touch also triggers reveal path */}>
                <ScratchCard
                  revealed={revealed}
                  awarded={awarded}
                  coinType="gifto"
                  onReveal={handleReveal}
                  loading={revealing && !revealed}
                />
              </div>
              {revealed && (
                <p className="text-xs text-emerald-300 mt-2 font-semibold" data-testid="scratch-success-note">
                  ✨ Gifto Coins credited to your wallet
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2 w-full">
              <Button
                onClick={shopNow}
                className="w-full rounded-full bg-gradient-to-r from-magenta-500 to-magenta-600 hover:from-magenta-600 hover:to-magenta-700 text-white font-semibold py-5 shadow-lg"
                data-testid="celebration-shop-now"
              >
                <ShoppingBag className="w-4 h-4 mr-2" /> Shop Now <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={shareWa}
                  variant="ghost"
                  className="rounded-full text-white/90 hover:bg-white/10 hover:text-white text-xs h-9"
                  data-testid="celebration-share"
                >
                  Share 💜
                </Button>
                <Button
                  onClick={dismiss}
                  variant="ghost"
                  className="rounded-full text-white/70 hover:bg-white/10 hover:text-white text-xs h-9"
                  data-testid="celebration-dismiss"
                >
                  Later
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
